import EditStepAction from "./EditStepAction.actions"
import { SvelteMap } from "svelte/reactivity"
import ExecuteStepNowAction from "./ExecuteStepNowAction.actions"
import LetterStepActions from "./LetterStepActions.actions"
import RetryStepAction from "./RetryStepAction.actions"
import StepProgressionAction from "./StepProgressionAction.actions"
import WerkAdminStepAction from "./WerkAdminStepAction.actions"
import type { ContactOrderStepActionsProps } from "../_logic/contract"
import { mdiBugPlay } from "@mdi/js"
import type { TableRowAction } from "@kontextwerk/web-sdk/core/layout/table"
import { isTerminalStepStatus } from "../_shared/_logic/action-availability"
import type { ComponentProps } from "svelte"
import type TestStepDialog from "../_modules/test-step-dialog"
type TestContext = Omit<ComponentProps<typeof TestStepDialog>, "onClose">
type ActionState = {
  executing: boolean
  retrying: boolean
  markingDue: boolean
  pending: boolean
  resolved: boolean
}

export function createContactOrderStepActions() {
  const states = new SvelteMap<string, Partial<ActionState>>()
  let testContext = $state.raw<TestContext | null>(null)
  function stateFor(key: string) {
    return {
      get executing() {
        return states.get(key)?.executing ?? false
      },
      set executing(value: boolean) {
        states.set(key, { ...states.get(key), executing: value })
      },
      get retrying() {
        return states.get(key)?.retrying ?? false
      },
      set retrying(value: boolean) {
        states.set(key, { ...states.get(key), retrying: value })
      },
      get markingDue() {
        return states.get(key)?.markingDue ?? false
      },
      set markingDue(value: boolean) {
        states.set(key, { ...states.get(key), markingDue: value })
      },
      get pending() {
        return states.get(key)?.pending ?? false
      },
      set pending(value: boolean) {
        states.set(key, { ...states.get(key), pending: value })
      },
      get resolved() {
        return states.get(key)?.resolved ?? false
      },
      set resolved(value: boolean) {
        states.set(key, { ...states.get(key), resolved: value })
      },
    }
  }
  function build({
    row,
    order,
    organizationId,
    organizationSlug = null,
    contactPoints = [],
    customers = [],
    availableChannels = [],
    communicationsByStepId = new Map(),
    automationContext = null,
    voiceLikeChannels,
    capabilities,
    onApplyStepPatch = () => {},
    onContactOrderUpdated = () => {},
    onContactOrderStepUpdated = () => {},
    onReloadContactOrders,
    onReloadOutreach,
    onExecuteNowStarted = () => {},
  }: ContactOrderStepActionsProps): TableRowAction[] {
    const actionOrder = row.stepActionContext?.order ?? order
    const caseId = actionOrder.case_id ?? ""
    const contactOrderId = actionOrder.id
    const step =
      row.stepActionContext?.step ??
      row.step ??
      (row.source === "planned"
        ? (actionOrder.steps?.find((entry) => entry.id === row.id) ?? null)
        : null)
    const stepId = step?.id ?? row.step?.id ?? (row.source === "planned" ? row.id : "")
    const targetContactPointUsable = row?.targetContactPointUsable !== false
    const state = stateFor(contactOrderId + ":" + stepId)
    const actions: TableRowAction[] = []

    if (
      capabilities.can_test_step &&
      ["EMAIL", "MOBILE_MESSAGE", "PHONE"].includes(String(row.channel || "").toUpperCase()) &&
      !isTerminalStepStatus(step?.status ?? row.status)
    ) {
      actions.push({
        id: "test",
        label: "Kontaktschritt testen",
        tooltip:
          "Kontaktschritt mit einer Testadresse ausführen, ohne den Ankündigungsplan fortzuschreiben.",
        icon: mdiBugPlay,
        onClick: () => {
          testContext = { row, step, stepId, caseId, contactOrderId, allowed: true }
        },
      })
    }

    actions.push(
      ...WerkAdminStepAction({
        row: row,
        step: step,
        automationContext: automationContext,
        organizationSlug: organizationSlug,
        voiceLikeChannels: voiceLikeChannels,
        allowed: Boolean(capabilities.can_open_automation),
      }).map((action) => ({ ...action, id: "WerkAdminStepAction." + action.id })),
    )

    actions.push(
      ...LetterStepActions(
        {
          row: row,
          order: actionOrder,
          stepId: stepId,
          caseId: caseId,
          contactOrderId: contactOrderId,
          organizationId: organizationId,
          targetContactPointUsable: targetContactPointUsable,
          canManageLetter: Boolean(capabilities.can_manage_letter),
          onApplyStepPatch: onApplyStepPatch,
          onContactOrderStepUpdated: onContactOrderStepUpdated,
          onReloadOutreach: onReloadOutreach,
        },
        state,
      ).map((action) => ({ ...action, id: "LetterStepActions." + action.id })),
    )

    actions.push(
      ...ExecuteStepNowAction(
        {
          row: row,
          order: actionOrder,
          step: step,
          stepId: stepId,
          caseId: caseId,
          contactOrderId: contactOrderId,
          organizationId: organizationId,
          organizationSlug: organizationSlug,
          contactPoints: contactPoints,
          customers: customers,
          availableChannels: availableChannels,
          communicationsByStepId: communicationsByStepId,
          targetContactPointUsable: targetContactPointUsable,
          canExecuteNow: Boolean(capabilities.can_execute_now),
          onApplyStepPatch: onApplyStepPatch,
          onContactOrderUpdated: onContactOrderUpdated,
          onContactOrderStepUpdated: onContactOrderStepUpdated,
          onReloadContactOrders: onReloadContactOrders,
          onReloadOutreach: onReloadOutreach,
          onExecuteNowStarted: onExecuteNowStarted,
        },
        state,
      ).map((action) => ({ ...action, id: "ExecuteStepNowAction." + action.id })),
    )

    actions.push(
      ...RetryStepAction(
        {
          row: row,
          order: actionOrder,
          step: step,
          stepId: stepId,
          caseId: caseId,
          contactOrderId: contactOrderId,
          organizationId: organizationId,
          organizationSlug: organizationSlug,
          contactPoints: contactPoints,
          customers: customers,
          availableChannels: availableChannels,
          communicationsByStepId: communicationsByStepId,
          targetContactPointUsable: targetContactPointUsable,
          canRetry: Boolean(capabilities.can_retry),
          onApplyStepPatch: onApplyStepPatch,
          onContactOrderUpdated: onContactOrderUpdated,
          onContactOrderStepUpdated: onContactOrderStepUpdated,
          onReloadContactOrders: onReloadContactOrders,
          onReloadOutreach: onReloadOutreach,
        },
        state,
      ).map((action) => ({ ...action, id: "RetryStepAction." + action.id })),
    )

    actions.push(
      ...StepProgressionAction(
        {
          row: row,
          order: actionOrder,
          step: step,
          stepId: stepId,
          caseId: caseId,
          contactOrderId: contactOrderId,
          organizationId: organizationId,
          organizationSlug: organizationSlug,
          contactPoints: contactPoints,
          customers: customers,
          availableChannels: availableChannels,
          communicationsByStepId: communicationsByStepId,
          targetContactPointUsable: targetContactPointUsable,
          canSkipWait: Boolean(capabilities.can_skip_wait),
          canCancelStep: Boolean(capabilities.can_cancel_step),
          onApplyStepPatch: onApplyStepPatch,
          onContactOrderUpdated: onContactOrderUpdated,
          onContactOrderStepUpdated: onContactOrderStepUpdated,
          onReloadContactOrders: onReloadContactOrders,
          onReloadOutreach: onReloadOutreach,
        },
        state,
      ).map((action) => ({ ...action, id: "StepProgressionAction." + action.id })),
    )

    actions.push(
      ...EditStepAction({
        row: row,
        order: actionOrder,
        step: step,
        stepId: stepId,
        caseId: caseId,
        contactOrderId: contactOrderId,
        organizationId: organizationId,
        organizationSlug: organizationSlug,
        contactPoints: contactPoints,
        customers: customers,
        availableChannels: availableChannels,
        communicationsByStepId: communicationsByStepId,
        targetContactPointUsable: targetContactPointUsable,
        canEditStep: Boolean(capabilities.can_edit_step),
        onApplyStepPatch: onApplyStepPatch,
        onContactOrderUpdated: onContactOrderUpdated,
        onContactOrderStepUpdated: onContactOrderStepUpdated,
        onReloadContactOrders: onReloadContactOrders,
        onReloadOutreach: onReloadOutreach,
      }).map((action) => ({ ...action, id: "EditStepAction." + action.id })),
    )

    return actions
  }

  return {
    build,
    get testContext() {
      return testContext
    },
    closeTest: () => {
      testContext = null
    },
  }
}
